import React, { forwardRef, useImperativeHandle, useMemo, useCallback, useEffect, useRef as useRefHook } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import MedicationEntry from './MedicationEntry'
import { defaultMedicationValues, priorityOptions } from './constants'
import { buildProductMap, getProgramOptions, resolveSubmitValues } from './utils'
import type { RxPadFormRef, RxPadFormProps, RxPadFormValues } from './types'

const medicationSchema = z.object({
  medication: z.string().min(1, 'Please select or enter a medication.'),
  dose: z.string().min(1, 'Please select or enter a dose.'),
  refills: z.string().min(1, 'Please enter refills.'),
  quantity: z.string().min(1, 'Please select or enter quantity.'),
  unit: z.string().min(1, 'Please select a unit.'),
  sig: z.string().min(1, 'Please select or enter instruction.'),
  reason: z.string().min(1, 'Please select or enter reason for compound.'),
  additionalContext: z.string().optional().default(''),
  daysSupply: z.string().optional().default(''),
  flavor: z.string().optional().default(''),
  notes: z.string().optional().default(''),
  program: z.string().optional().default(''),
  program_id: z.string().optional().default(''),
  doNotFill: z.boolean().default(false),
  doNotRefill: z.boolean().default(false),
  refillNeeded: z.boolean().default(false),
  patientContactRequested: z.boolean().default(false),
})

const formSchema = z.object({
  medications: z.array(medicationSchema).min(1),
  priority: z.number().default(1),
})

const RxPadForm = forwardRef<RxPadFormRef, RxPadFormProps>(
  ({ prescriptionProducts, programs, initialValues }, ref) => {
    const form = useForm<RxPadFormValues>({
      resolver: zodResolver(formSchema),
      defaultValues: initialValues || {
        medications: [{ ...defaultMedicationValues }],
        priority: 1,
      },
      mode: 'onTouched',
    })

    // Reset form when initialValues changes (e.g. after products load in edit mode)
    const prevInitialRef = useRefHook<string | null>(null)
    useEffect(() => {
      if (initialValues) {
        const key = JSON.stringify(initialValues)
        if (key !== prevInitialRef.current) {
          prevInitialRef.current = key
          form.reset(initialValues)
        }
      }
    }, [initialValues, form])

    const { fields, append, remove } = useFieldArray({
      control: form.control,
      name: 'medications',
    })

    const productMap = useMemo(
      () => buildProductMap(prescriptionProducts),
      [prescriptionProducts]
    )

    const programOptions = useMemo(() => getProgramOptions(programs), [programs])

    const scrollToFirstError = useCallback(() => {
      // Find first error field and scroll to it
      const errorKeys = Object.keys(form.formState.errors.medications || {})
      if (errorKeys.length > 0) {
        const firstIndex = errorKeys[0]
        const medErrors = (form.formState.errors.medications as any)?.[firstIndex]
        if (medErrors) {
          const fieldOrder = [
            'medication', 'dose', 'refills', 'quantity', 'unit', 'sig', 'reason',
          ]
          for (const field of fieldOrder) {
            if (medErrors[field]) {
              const el = document.querySelector(
                `[name="medications.${firstIndex}.${field}"]`
              ) as HTMLElement
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                el.focus()
                return
              }
            }
          }
        }
      }
    }, [form.formState.errors])

    const submitWithValidation = useCallback(async (): Promise<RxPadFormValues | null> => {
      const isValid = await form.trigger()
      if (!isValid) {
        scrollToFirstError()
        return null
      }
      const values = form.getValues()
      const resolved = resolveSubmitValues(values.medications, productMap)
      return { medications: resolved, priority: values.priority }
    }, [form, productMap, scrollToFirstError])

    useImperativeHandle(ref, () => ({
      submit: submitWithValidation,
      getValues: () => {
        const values = form.getValues()
        const resolved = resolveSubmitValues(values.medications, productMap)
        return { medications: resolved, priority: values.priority }
      },
      reset: () => {
        form.reset({
          medications: [{ ...defaultMedicationValues }],
          priority: 1,
        })
      },
    }))

    return (
      <div className="space-y-4">
        {fields.map((field, index) => (
          <MedicationEntry
            key={field.id}
            index={index}
            form={form}
            productMap={productMap}
            programOptions={programOptions}
            programs={programs}
            canRemove={index > 0}
            onRemove={() => remove(index)}
          />
        ))}

        {/* Add Medication Button */}
        <Button
          type="button"
          variant="outline"
          onClick={() => append({ ...defaultMedicationValues })}
          className="w-full border-dashed border-slate-300 text-slate-600 hover:border-brand-teal hover:text-brand-teal"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Medication
        </Button>

        {/* Priority */}
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <Label className="mb-1.5 block text-xs font-medium text-slate-500">Priority</Label>
          <Select
            value={String(form.watch('priority'))}
            onValueChange={(v) => form.setValue('priority', Number(v))}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select priority" />
            </SelectTrigger>
            <SelectContent>
              {priorityOptions.map((opt) => (
                <SelectItem key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    )
  }
)

RxPadForm.displayName = 'RxPadForm'

export default RxPadForm
export type { RxPadFormRef, RxPadFormProps }
